"use client";

import { useRouter } from "next/navigation";

import ErrorMessage from "@/app/component/error-message";
import PrimaryButtonGradient from "@/app/component/gradientbutton";
import { InputTextBox } from "@/app/component/InputTextBox";
import MobileSelect from "@/app/component/mobileselect/mobileselectinput";
import ReCaptchaComponent from "@/app/component/recaptcha";
import { useClientConfigProvider } from "@/app/config/client-config-provider";
import { EventLabels } from "@/app/constants/mix-panel-enums";
import AppRoutes from "@/app/constants/routes";
import { useMixPanel } from "@/app/context/mixPanelContext";
import isLoggedIn from "@/app/redirect";
import { CustomToastEnum, useCustomToast } from "@/app/toast/custom-toast";
import ToastUtils from "@/app/toast/toast-utils";
import {
  UniversalStorageConstants,
  UniversalStorageUtils,
  UseQueryResult,
  useAppContext,
  useQuery,
} from "@thriwe-2-0/thriwe-ui-utils";
import { useFormik } from "formik";
import Image from "next/image";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import { SignInUI } from "./components/common-signin-ui";
import { VerifiedComponent } from "./components/verified-ui";

export interface LoginInfo {
  countryCode?: string;
  mobile?: string;
  token?: string;
  email?: string;
  fromWhere: string;
}
export interface CountryCodeType {
  code: string;
  flag: any;
  currency: string;
  name: string;
}

export enum LoginType {
  Mobile,
  Email,
  MobileAndEmail,
}

function LoginWithPassword({
  captcha = false,
  placeHolderText,
  toastType,
  islogin = true,
}: any) {
  const context = useAppContext();
  const { trackEvent } = useMixPanel();
  const {
    instances: { apiRepository },
    fetchUserProfile,
  } = context;
  const { config, getImagePath, StringConstantsInstance } =
    useClientConfigProvider();
  const router = useRouter();

  const [countryCodesArr, setCountryCodeArr] = useState<CountryCodeType[]>([]);
  const [countryCode, setCountryCode] = useState("");
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [mobileNumber, setMobileNumber] = useState<string>();
  const [email, setEmail] = useState<string>();
  const [isVerified, setIsVerified] = useState<boolean>(false);

  const [switchSignInType, setswitchSignInType] = useState<string>();
  const [validationSchema, setValidationSchema] = useState<any>(null);
  const { renderError, showErrorToast } = useCustomToast(
    toastType ?? CustomToastEnum.Toast
  );
  const [shouldPasswordVisible, setShouldPasswordVisible] = useState(true);

  useEffect(() => {
    if (switchSignInType == "email" && islogin) {
      trackEvent(EventLabels.SL_Login_Email, { screen: "Login" });
      trackEvent(EventLabels.SL_Login_Email_Done, { screen: "Login" });
    }
  }, [switchSignInType, trackEvent]);

  useEffect(() => {
    if (!switchSignInType) {
      setswitchSignInType(Object.keys(config?.login?.fields ?? {})?.[0]);
    }
    if (config) {
      if (switchSignInType == "mobile" && config?.login?.fields?.mobile) {
        setValidationSchema(
          Yup.object().shape({
            mobile: Yup.string()
              .required(`${config?.login?.fields?.mobile?.validationText}`)
              .matches(
                config?.login?.fields?.mobile?.validationRegex,
                config?.login?.fields?.mobile?.validationText
              ),
          })
        );
      } else if (switchSignInType == "email" && config?.login?.fields?.email) {
        setValidationSchema(
          Yup.object().shape({
            email: Yup.string()
              .required("Email is a required field")
              .matches(
                config?.login?.fields?.email?.validationRegex,
                config?.login?.fields?.email?.validationText
              ),
          })
        );
      }
      (() => signUpForm.validateForm())();
      signUpForm.setFieldValue(
        switchSignInType == "mobile" ? "email" : "mobile",
        ""
      );
    }
  }, [switchSignInType, config]);

  const handleFormSubmit = (values: any) => {
    if (!shouldPasswordVisible) {
      setIsLoading(true);
      apiRepository
        ?.checkUser({
          email: values?.email,
          phone: values?.mobile.toString(),
          countryCode: countryCode?.toString(),
        })
        .then((v) => {
          if (v.data.exists == true) {
            setShouldPasswordVisible(true);
          } else if (v.data.exists == false) {
            const searchParams = new URLSearchParams();
            if (values?.mobile.toString()) {
              searchParams.append("phoneNumber", values?.mobile.toString());
              searchParams.append("countryCode", countryCode?.toString());
            }

            if (values?.email.toString()) {
              searchParams.append("email", values?.email.toString());
            }
            router.push(AppRoutes.register + "?" + searchParams);
          } else {
            ToastUtils.showErrorToast("Something went wrong");
          }

          setIsLoading(false);
        })
        .catch((e) => {
          setIsLoading(false);
          if (e?.response?.data?.message) {
            ToastUtils.showErrorToast(e?.response?.data?.message);
          } else {
            ToastUtils.showErrorToast("Something went wrong");
          }
        });
      return;
    }

    if (captcha) {
      if (captchaValue === null || captchaValue == "") {
        showErrorToast("please tick captcha");
        return;
      }
    }
    setIsLoading(true);
    const mobileNumber = values.mobile;
    setEmail(values?.email);
    setMobileNumber(mobileNumber);
    setIsLoading(true);
    apiRepository
      ?.loginRegisterPassword({
        email: values?.email,
        phone: values?.mobile.toString(),
        countryCode: countryCode.toString(),
        password: values?.password?.toString(),
      })
      .then((v) => {
        setIsLoading(false);
        let response = v?.data.user;

        UniversalStorageUtils.setItem(
          config?.nativeDataSavingType,
          UniversalStorageConstants.AUTH_TOKEN,
          v.headers.authorization
        );

        setTimeout(() => {
          router.push(AppRoutes.dashboard);
        }, 1500);
        setTimeout(() => {
          setIsVerified(true);
        }, 500);

        fetchUserProfile();
      })
      .catch((e) => {
        setIsLoading(false);
        console.log("login error response", e?.response?.data);
        if (e?.response?.data?.message) {
          ToastUtils.showErrorToast(e?.response?.data?.message);
        } else {
          ToastUtils.showErrorToast("Something went wrong");
        }
      });
  };
  const item = {
    name: "Mobile Number",
    variableName: "mobile",
    editable: true,
    type: "number",
    labelName: "",
  };
  const signUpForm = useFormik({
    initialValues: {
      mobile: "",
      email: "",
      password: "",
    },
    isInitialValid: false,
    validationSchema: validationSchema,
    onSubmit: handleFormSubmit,
    enableReinitialize: true,
    validateOnBlur: true,
    validateOnMount: true,
    validateOnChange: true,
  });

  const {
    setFieldValue,
    values,
    handleBlur,
    handleChange,
    submitForm,
    touched,
    errors,
    isValid,
    validateOnChange,
  } = signUpForm;

  const convertData = (data: any) => {
    return data.map((item: any, index: number) => ({
      value: item.code,
      label: item.name,
      flag: item?.flag,
      data: item,
    }));
  };

  const useCountriesQuery = (): UseQueryResult<any, Error> =>
    useQuery<any, Error>({
      queryKey: ["countries"],
      queryFn: async () => {
        try {
          const response = await apiRepository?.getCountryCode({});
          return response;
        } catch (e) {
          throw new Error("Failed to fetch countries");
        }
      },
      gcTime: 120000,
      staleTime: 120000,
    });

  const { data: countriesData } = useCountriesQuery();

  useEffect(() => {
    if (countriesData?.data) {
      const sortCountriesByName = (countries: CountryCodeType[]) => {
        return countries;
      };
      const sortedCountries = sortCountriesByName(countriesData?.data);
      setCountryCode(sortedCountries?.[0]?.code);
      const convertedData = convertData(sortedCountries);
      setCountryCodeArr(convertedData);
    }
  }, [countriesData]);

  const handleDivClick = (handleFormSubmit: any) => {
    handleFormSubmit();
    trackEvent(islogin ? EventLabels.CTA_LOGIN : EventLabels.CTA_Registration, {
      meta:
        switchSignInType == "email" ? "Login with Email" : "Login with Mobile",
      screen: "Login",
    });
  };

  const loginUI = () => {
    return (
      <div
        className="login-side-container login-size"
        style={{ textAlign: "start", borderRadius: "16px", zIndex: "10" }}
      >
        {!isVerified ? (
          <div className="login-flex-container-password">
            <SignInUI
              switchSignInType={switchSignInType}
              setswitchSignInType={setswitchSignInType}
              islogin={islogin}
            />

            <div className="input-label-group login-flex-container-item">
              <div className="label" style={{ marginBottom: "0px" }}>
                Enter Your{" "}
                {switchSignInType == "mobile"
                  ? " Phone number"
                  : " Email address"}
              </div>

              {switchSignInType == "mobile" &&
                config?.login?.fields?.mobile && (
                  <>
                    <div>
                      <div
                        className="mobile-select"
                        style={{ display: "flex", flexDirection: "row" }}
                      ></div>
                      <MobileSelect
                        handleChange={(e: any) => {
                          handleChange(e);
                        }}
                        handleBlur={handleBlur}
                        formik={signUpForm}
                        countryCode={countryCode}
                        countryCodesArr={countryCodesArr}
                        setCountryCode={setCountryCode}
                        errors={errors}
                        values={values}
                        touched={touched}
                        item={item}
                        hasError={
                          touched?.mobile != null && errors?.mobile != null
                        }
                      />
                      <div style={{ marginTop: "3px" }}>
                        <ErrorMessage
                          message={
                            touched?.mobile && errors?.mobile
                              ? errors?.mobile ?? ""
                              : undefined
                          }
                          touched={touched?.mobile}
                        />
                      </div>
                    </div>
                  </>
                )}

              {switchSignInType == "email" && config?.login?.fields?.email && (
                <div>
                  <InputTextBox
                    hint={placeHolderText}
                    onChange={(e: any) => {
                      setShouldPasswordVisible(false);
                      handleChange(e);
                    }}
                    onBlur={handleBlur}
                    name="email"
                    value={values.email}
                    placeholder={"Email address"}
                    hasError={touched?.email != null && errors?.email != null}
                  />
                  <div style={{ marginTop: "3px" }}>
                    <ErrorMessage
                      message={touched?.email ? errors?.email ?? "" : undefined}
                      touched={touched?.email}
                    />
                  </div>
                </div>
              )}
            </div>

            {config?.login?.disclaimer && (
              <div>
                <div className="termscondition-font disclaimer-icon">
                  <Image
                    className="c-pointer termscondition-checkbox"
                    id="terms&condition"
                    alt=""
                    src={getImagePath("info-landing.png")}
                    width={18}
                    height={18}
                  />
                  <label>{StringConstantsInstance?.getDisclaimerLogin}</label>
                </div>
              </div>
            )}

            {shouldPasswordVisible && (
              <div>
                <InputTextBox
                  hint={placeHolderText}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="password"
                  value={values.password}
                  placeholder={"Password"}
                  hasError={
                    touched?.password != null && errors?.password != null
                  }
                  type="password"
                  autoComplete="new-password"
                />
                <div style={{ marginTop: "3px" }}>
                  <ErrorMessage
                    message={
                      touched?.password ? errors?.password ?? "" : undefined
                    }
                    touched={touched?.password}
                  />
                </div>
              </div>
            )}

            {captcha && (
              <div>
                <ReCaptchaComponent
                  onChange={(value) => {
                    setCaptchaValue(value);
                  }}
                />
              </div>
            )}

            {shouldPasswordVisible && (
              <div
                className="forgot-password-visa"
                onClick={() => {
                  router.push(AppRoutes.forgotPassword);
                }}
              >
                Forgot password?
              </div>
            )}

            {/* {renderError()} */}
            <div className="button-container">
              <PrimaryButtonGradient
                padding="0px !important"
                name={
                  islogin
                    ? config?.login?.loginButton?.text ?? "Sign In"
                    : config?.login?.signupButton?.text ?? "Sign Up"
                }
                isDisabled={!isValid}
                fullWidth={true}
                onClick={() => handleDivClick(submitForm)}
                isLoading={isLoading}
                classNameDiv="buttonDiv"
                secondary={false}
                arrowIcon={
                  <span>
                    <Image
                      src={getImagePath("arrow-forward.png")}
                      alt=""
                      width="13"
                      height="13"
                      style={{
                        width: "auto",
                        height: "13px",
                      }}
                    />
                  </span>
                }
                classNameBackgroundStylePrimaryGradient="buttonBackgroundStylePrimaryGradient2"
                classNamePrimaryIsLoading="buttonBackgroundStylePrimaryGradient2"
              />
            </div>
          </div>
        ) : (
          <VerifiedComponent />
        )}
      </div>
    );
  };

  return loginUI();
}

export default isLoggedIn(LoginWithPassword);
